import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const PayInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Get invoice details from location state (passed from Invoices page)
  const invoice = location.state || {};

  const [cardNumber, setCardNumber] = useState('');
  const [expDate, setExpDate] = useState('');
  const [cvc, setCvc] = useState('');
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vendor, setVendor] = useState({ firstName: '', lastName: '' });
  
  // New states for credit card selection
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingCards, setLoadingCards] = useState(true);

  // Get customer name from logged in user
  const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
  const customerName = `${loggedInUser.firstName || ''} ${loggedInUser.lastName || ''}`.trim();
  const userId = loggedInUser.id;

  useEffect(() => {
    // Fetch vendor info based on vendor_id
    if (invoice.vendor_id) {
  fetch(`${process.env.REACT_APP_}/vendor?vendor_id=${invoice.vendor_id}`)
        .then(res => res.json())
        .then(data => {
          if (data.vendor) {
            setVendor({
              firstName: data.vendor.firstName || '',
              lastName: data.vendor.lastName || '',
            });
          }
        });
    }

    // Fetch saved credit cards
    if (userId) {
  fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards?user_id=${userId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.cards) {
            setSavedCards(data.cards);
            // Auto-select default card if available
            const defaultCard = data.cards.find(card => card.is_default);
            if (defaultCard) {
              setSelectedCardId(defaultCard.id.toString());
            } else if (data.cards.length === 0) {
              setUseNewCard(true); // Force new card entry if no saved cards
            }
          }
          setLoadingCards(false);
        })
        .catch(err => {
          console.error('Error fetching saved cards:', err);
          setUseNewCard(true); // Fallback to manual entry
          setLoadingCards(false);
        });
    }
  }, [invoice.vendor_id, userId]);

  const serviceTotal = Number(invoice.total) / 1.1;
  const bookingFee = (serviceTotal * 0.10).toFixed(2);
  const grandTotal = Number(invoice.total).toFixed(2);

  const handleCardSelection = (e) => {
    const value = e.target.value;
    if (value === 'new') {
      setUseNewCard(true);
      setSelectedCardId('');
      setCardNumber('');
      setExpDate('');
      setCvc('');
    } else {
      setUseNewCard(false);
      setSelectedCardId(value);
      // Clear manual entry fields when using saved card
      setCardNumber('');
      setExpDate('');
      setCvc('');
    }
  };

  const maskCardNumber = (cardNumber) => {
    if (!cardNumber) return '';
    const lastFour = cardNumber.slice(-4);
    return `**** **** **** ${lastFour}`;
  };

  const getCardBrand = (cardNumber) => {
    if (!cardNumber) return '';
    const firstDigit = cardNumber.charAt(0);
    switch (firstDigit) {
      case '4': return 'Visa';
      case '5': return 'Mastercard';
      case '3': return 'American Express';
      default: return 'Card';
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setPaying(true);
    setError('');
    setSuccess('');

    let paymentCardNumber = cardNumber;
    let paymentExpDate = expDate;
    let paymentCvc = cvc;

    // If using saved card, fetch the card details
    if (!useNewCard && selectedCardId) {
      try {
  const cardRes = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards/${selectedCardId}?user_id=${userId}`);
        const cardData = await cardRes.json();
        if (cardRes.ok && cardData.success) {
          paymentCardNumber = cardData.card.card_number;
          paymentExpDate = `${cardData.card.expiry_month}/${cardData.card.expiry_year.slice(-2)}`;
          paymentCvc = cardData.card.cvv;
        } else {
          setError('Error retrieving saved card details.');
          setPaying(false);
          return;
        }
      } catch (err) {
        setError('Error retrieving saved card details.');
        setPaying(false);
        return;
      }
    }

    // Validation for manual entry
    if (useNewCard) {
      if (!paymentCardNumber || !paymentExpDate || !paymentCvc) {
        setError('Please fill in all card details.');
        setPaying(false);
        return;
      }
    }

    try {
      // Call invoice post API before payment
  const invoiceRes = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: invoice.customer_id,
          vendor_id: invoice.vendor_id,
          service_detail_id: invoice.service_detail_id || invoice.product_detail_id,
          status: 'paid',
          date: invoice.date,
          start_time: invoice.start_time,
          end_time: invoice.end_time,
          amount: grandTotal,
          appointment_id: id,
        }),
      });
      const invoiceData = await invoiceRes.json();
      if (!invoiceRes.ok || !invoiceData.success) {
        setError(invoiceData.message || 'Invoice creation failed.');
        setPaying(false);
        return;
      }

      // Simulate payment
  const res = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/payinvoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: invoice.customer_id,
          vendor_id: invoice.vendor_id,
          total: grandTotal,
          start_time: invoice.start_time,
          appointment_id: invoice.appointment_id,
          service_detail_id: invoice.service_detail_id || invoice.product_detail_id,
          end_time: invoice.end_time,
          date: invoice.date,
          cardNumber: paymentCardNumber,
          expDate: paymentExpDate,
          cvc: paymentCvc,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess('Payment successful!');
        setTimeout(() => navigate('/invoices'), 2000);
      } else {
        setError(data.message || 'Payment failed.');
      }
    } catch {
      setError('Payment failed.');
    }
    setPaying(false);
  };

  if (loadingCards) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading payment options...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Pay Invoice</h2>
      <div style={styles.card}>
        <div style={styles.details}>
          <p>
            <strong>Customer Name:</strong> {customerName}
          </p>
          <p>
            <strong>Vendor Name:</strong> {vendor.firstName} {vendor.lastName}
          </p>
          <p><strong>Date:</strong> {invoice.date}</p>
          <p><strong>Start Time:</strong> {invoice.start_time}</p>
          <p><strong>End Time:</strong> {invoice.end_time}</p>
          <p>
            <strong>Service Fee:</strong> ${serviceTotal.toFixed(2)}
          </p>
          <p>
            <strong>Booking Fee (10%):</strong> ${bookingFee}
          </p>
          <p>
            <strong>Total:</strong> ${grandTotal}
          </p>
        </div>
        
        <form onSubmit={handlePay} style={styles.form}>
          <h4 style={{ marginBottom: '18px' }}>Payment Method</h4>
          
          {/* Credit Card Selection Dropdown */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>Select Payment Method:</label>
            <select
              value={useNewCard ? 'new' : selectedCardId}
              onChange={handleCardSelection}
              style={styles.select}
              required
            >
              <option value="">Choose payment method...</option>
              {savedCards.map(card => (
                <option key={card.id} value={card.id.toString()}>
                  {getCardBrand(card.card_number)} {maskCardNumber(card.card_number)} - {card.cardholder_name}
                  {card.is_default && ' (Default)'}
                </option>
              ))}
              <option value="new">Enter new card details</option>
            </select>
          </div>

          {/* Manual Card Entry (shown when useNewCard is true) */}
          {useNewCard && (
            <div style={styles.newCardSection}>
              <h5 style={{ marginBottom: '12px', color: '#4078c0' }}>Enter Card Details</h5>
              <input
                type="text"
                placeholder="Card Number"
                value={cardNumber}
                onChange={e => setCardNumber(e.target.value)}
                style={styles.input}
                required
                maxLength={19}
              />
              <input
                type="text"
                placeholder="MM/YY"
                value={expDate}
                onChange={e => setExpDate(e.target.value)}
                style={styles.input}
                required
                maxLength={5}
              />
              <input
                type="text"
                placeholder="CVC"
                value={cvc}
                onChange={e => setCvc(e.target.value)}
                style={styles.input}
                required
                maxLength={4}
              />
            </div>
          )}

          {/* Show selected card info */}
          {!useNewCard && selectedCardId && (
            <div style={styles.selectedCardInfo}>
              {(() => {
                const selectedCard = savedCards.find(card => card.id.toString() === selectedCardId);
                return selectedCard ? (
                  <div style={styles.cardPreview}>
                    <strong>Selected Card:</strong> {getCardBrand(selectedCard.card_number)} {maskCardNumber(selectedCard.card_number)}
                    <br />
                    <strong>Cardholder:</strong> {selectedCard.cardholder_name}
                    <br />
                    <strong>Expires:</strong> {selectedCard.expiry_month}/{selectedCard.expiry_year}
                  </div>
                ) : null;
              })()}
            </div>
          )}

          <button style={styles.button} type="submit" disabled={paying || (!useNewCard && !selectedCardId)}>
            {paying ? 'Processing...' : 'Pay Now'}
          </button>
          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}
        </form>
        
        <div style={styles.actionButtons}>
          <button style={styles.backBtn} onClick={() => navigate('/invoices')}>
            Back to Invoices
          </button>
          <button style={styles.manageCardsBtn} onClick={() => navigate('/creditcards')}>
            Manage Cards
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '600px',
    margin: '40px auto',
    padding: '32px',
    background: '#fff',
    borderRadius: '14px',
    boxShadow: '0 4px 24px rgba(141,110,54,0.10)',
  },
  title: {
    marginBottom: '28px',
    color: '#4078c0',
    fontSize: '1.7rem',
    textAlign: 'center',
    fontWeight: 'bold',
    letterSpacing: '1px',
  },
  loading: {
    textAlign: 'center',
    color: '#4078c0',
    fontSize: '1.2rem',
    marginTop: '40px',
  },
  card: {
    background: '#f5f6fa',
    borderRadius: '14px',
    boxShadow: '0 1px 8px #e0cfa0',
    padding: '28px 22px 22px 22px',
    minHeight: '220px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: '2px solid #e0cfa0',
    position: 'relative',
  },
  details: {
    marginBottom: '18px',
    fontSize: '1.08rem',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inputGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 'bold',
    color: '#333',
  },
  select: {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
  },
  newCardSection: {
    background: '#f0f8ff',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #4078c0',
    marginBottom: '12px',
  },
  selectedCardInfo: {
    background: '#e8f5e8',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #388e3c',
    marginBottom: '12px',
  },
  cardPreview: {
    color: '#333',
    fontSize: '0.95rem',
    lineHeight: '1.4',
  },
  input: {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    marginBottom: '8px',
    boxSizing: 'border-box',
  },
  button: {
    background: '#388e3c',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 22px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #c8e6c9',
    transition: 'background 0.2s',
    marginTop: '8px',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '20px',
    gap: '12px',
  },
  backBtn: {
    background: '#4078c0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
    textDecoration: 'none',
    flex: '1',
  },
  manageCardsBtn: {
    background: '#8d6e36',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #c8a96e',
    transition: 'background 0.2s',
    textDecoration: 'none',
    flex: '1',
  },
  error: {
    color: '#d32f2f',
    fontWeight: 'bold',
    marginTop: '8px',
    textAlign: 'center',
  },
  success: {
    color: '#388e3c',
    fontWeight: 'bold',
    marginTop: '8px',
    textAlign: 'center',
  },
};

export default PayInvoice;